// useIntlAccaLadder — LADDER-Intl rungs (R1–R5) + legs for the active
// tournament (founder ruling #1: a separate international rung
// surface). DOCTRINE: never fabricate — on error/empty the page
// renders its honest-empty state. Rungs are produced by the
// intl-acca-ladder-assembly pipeline; empty until WC2026 is ACTIVE
// and the back-test is GREEN.

import { useCallback, useEffect, useMemo, useState } from "react";
import { getBrowserClient } from "../supabase/browser.ts";
import { useIntlAccaTournaments } from "./useIntlAccaTournaments.ts";
import type { IntlAccaTournament } from "../db/intl-acca-types.ts";

export interface LadderLeg {
  id: string;
  match_id: string;
  market_type: string;
  selection: string;
  strikza_price: number;
  market_price_at_selection: number;
  implied_edge: number | null;
  clv_confidence: "HIGH" | "MEDIUM" | "SPECULATIVE";
  reasoning_note: string;
  agent_flags: string[];
  clv_pp: number | null;
  result: "WON" | "LOST" | "VOID" | null;
}
export interface LadderRung {
  id: string;
  rung_code: "R1" | "R2" | "R3" | "R4" | "R5";
  rung_label: string;
  target_min_odds: number;
  target_max_odds: number | null;
  combined_odds: number | null;
  status: "ACTIVE" | "WON" | "LOST" | "VOID";
  clv_score_final: number | null;
  generated_at: string;
  legs: LadderLeg[];
}

export interface IntlAccaLadderVM {
  loading: boolean;
  error: string | null;
  tournament: IntlAccaTournament | null;
  rungs: LadderRung[];
  matchLabels: Record<string, string>;
  refetch: () => Promise<void>;
}

export function useIntlAccaLadder(): IntlAccaLadderVM {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const supabase = useMemo(() => getBrowserClient() as any, []);
  const { tournaments, activeId } = useIntlAccaTournaments();
  const tournamentId = activeId ?? tournaments[0]?.id ?? null;
  const [s, setS] = useState<Omit<IntlAccaLadderVM, "refetch">>({
    loading: true, error: null, tournament: null, rungs: [], matchLabels: {},
  });

  const fetchAll = useCallback(async () => {
    if (!tournamentId) {
      setS({ loading: false, error: null, tournament: null, rungs: [], matchLabels: {} });
      return;
    }
    setS((p) => ({ ...p, loading: true, error: null }));
    try {
      const [tRes, rRes] = await Promise.all([
        supabase.from("intl_acca_tournaments").select("*").eq("id", tournamentId).maybeSingle(),
        supabase.from("intl_acca_ladder_rungs").select("*")
          .eq("tournament_id", tournamentId).eq("status", "ACTIVE")
          .order("rung_code", { ascending: true }),
      ]);
      if (tRes.error) throw tRes.error;
      const rungs = (rRes.data ?? []) as LadderRung[];
      const rungIds = rungs.map((r) => r.id);
      const lgRes = rungIds.length
        ? await supabase.from("intl_acca_ladder_legs").select("*").in("rung_id", rungIds)
        : { data: [], error: null };
      const legs = (lgRes.data ?? []) as Array<LadderLeg & { rung_id: string }>;
      const byRung = new Map<string, LadderLeg[]>();
      for (const l of legs) {
        if (!byRung.has(l.rung_id)) byRung.set(l.rung_id, []);
        byRung.get(l.rung_id)!.push(l);
      }
      const withLegs = rungs.map((r) => ({ ...r, legs: byRung.get(r.id) ?? [] }));

      // Resolve human match labels (home/away nation names).
      const matchIds = [...new Set(legs.map((l) => l.match_id))];
      const labels: Record<string, string> = {};
      if (matchIds.length) {
        const mRes = await supabase.from("intl_acca_matches")
          .select("match_id,home,away,home_slot,away_slot").in("match_id", matchIds);
        type MRow = {
          match_id: string; home: string | null; away: string | null;
          home_slot: string | null; away_slot: string | null;
        };
        const codes = new Set<string>();
        for (const m of (mRes.data ?? []) as MRow[]) {
          if (m.home) codes.add(m.home);
          if (m.away) codes.add(m.away);
        }
        const nRes = codes.size
          ? await supabase.from("nations").select("code,name").in("code", [...codes])
          : { data: [] };
        const nameBy: Record<string, string> = {};
        for (const n of (nRes.data ?? []) as Array<{ code: string; name: string }>) {
          nameBy[n.code] = n.name;
        }
        const side = (code: string | null, slot: string | null) =>
          (code && (nameBy[code] ?? code)) || slot || "TBD";
        for (const m of (mRes.data ?? []) as MRow[]) {
          labels[m.match_id] =
            `${side(m.home, m.home_slot)} v ${side(m.away, m.away_slot)}`;
        }
      }

      setS({
        loading: false, error: null,
        tournament: (tRes.data as IntlAccaTournament | null) ?? null,
        rungs: withLegs, matchLabels: labels,
      });
    } catch (e) {
      setS({
        loading: false, error: e instanceof Error ? e.message : String(e),
        tournament: null, rungs: [], matchLabels: {},
      });
    }
  }, [supabase, tournamentId]);

  useEffect(() => { void fetchAll(); }, [fetchAll]);
  return { ...s, refetch: fetchAll };
}
